/**
 * WeekView — vista Settimana del calendario. 7 colonne giorno (Lun→Dom) con:
 *   - MIT spuntabili + draggable + DateAssignPopover
 *   - Azioni con `data` specifica fissate al giorno
 *   - DnD MIT tra giorni della settimana
 * In fondo, lista delle Azioni della settimana (flottanti, senza data specifica)
 * con date-chip cliccabile per spostarle a un'altra settimana o fissarle a un giorno.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { applyOperations, invalidateCache } from '../../lib/api';
import { AREE } from '../../constants/areas';
import { GIORNI_FULL } from '../../lib/calendarConstants';
import { weekFromIsoDate, daysOfIsoWeek } from '../../lib/weekFromIsoDate';
import Checkbox from '../nav/Checkbox';
import DateAssignPopover from '../nav/DateAssignPopover';

export default function WeekView({ isoWeek, data, todayIso, onToggleMit, onToggleAzione, onReload }) {
  const azioni = data?.azioni || [];
  const mit = data?.mit || [];
  const esec = data?.esecuzione || {};
  const [dragOverDay, setDragOverDay] = useState(null);

  const mitByDay = useMemo(() => _groupByData(mit), [mit]);
  const azioniByDay = useMemo(() => _groupByData(azioni), [azioni]);
  const azioniFloating = useMemo(() => azioni.filter((a) => !a.data), [azioni]);
  const days = useMemo(() => daysOfIsoWeek(isoWeek), [isoWeek]);

  const reassignMit = async (mitId, currentData, newData) => {
    if (!newData || newData === currentData) return;
    try {
      await applyOperations([{ entita: 'mit', azione: 'aggiorna', id: mitId, campi: { data: newData } }]);
      invalidateCache('stats:closed-week');
      onReload();
      toast.success(`MIT spostata al ${newData}`, {
        duration: 4000,
        action: {
          label: 'Annulla',
          onClick: async () => {
            try {
              await applyOperations([{ entita: 'mit', azione: 'aggiorna', id: mitId, campi: { data: currentData } }]);
              onReload();
              toast.success('Ripristinato');
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Errore annullamento');
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Spostamento MIT fallito');
      throw e;
    }
  };

  const reassignAzione = async (azId, currentWk, newVal) => {
    if (!newVal || newVal === currentWk) return;
    const isDate = /^\d{4}-\d{2}-\d{2}$/.test(newVal);
    const campi = isDate
      ? { data: newVal, settimana: weekFromIsoDate(newVal) }
      : { settimana: newVal, data: null };
    const undoLabel = isDate ? `al ${newVal}` : `a ${newVal.slice(-3)}`;
    try {
      await applyOperations([{ entita: 'azione', azione: 'aggiorna', id: azId, campi }]);
      invalidateCache('stats:closed-week');
      onReload();
      toast.success(`Azione spostata ${undoLabel}`, {
        duration: 4000,
        action: {
          label: 'Annulla',
          onClick: async () => {
            try {
              await applyOperations([{ entita: 'azione', azione: 'aggiorna', id: azId, campi: { settimana: currentWk, data: null } }]);
              onReload();
              toast.success('Ripristinato');
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Errore annullamento');
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Spostamento azione fallito');
      throw e;
    }
  };

  return (
    <div data-testid="cal-settimana">
      <div className="mb-4 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.18em] text-soft">
        <span>Esecuzione: {esec.fatti ?? 0}/{esec.totale ?? 0}{esec.percentuale != null ? ` · ${esec.percentuale}%` : ''}</span>
        <span className="text-mute normal-case tracking-normal">· Trascina le MIT tra i giorni oppure clicca la data per spostare</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {days.map((iso, idx) => (
          <WeekDayColumn
            key={iso}
            iso={iso}
            idx={idx}
            isToday={iso === todayIso}
            isDropTarget={dragOverDay === iso}
            dayMit={mitByDay[iso] || []}
            dayAzioni={azioniByDay[iso] || []}
            isoWeek={isoWeek}
            onDragEnter={() => setDragOverDay(iso)}
            onDragLeave={() => setDragOverDay((d) => (d === iso ? null : d))}
            onDrop={async (payload) => {
              setDragOverDay(null);
              try {
                const { id, currentData } = JSON.parse(payload);
                await reassignMit(id, currentData, iso);
              } catch {
                toast.error('Drop payload non valido');
              }
            }}
            onToggleMit={onToggleMit}
            onToggleAzione={onToggleAzione}
            onReassignMit={reassignMit}
            onReassignAzione={reassignAzione}
          />
        ))}
      </div>

      <FloatingAzioniList
        azioniFloating={azioniFloating}
        azioniTotali={azioni}
        isoWeek={isoWeek}
        onToggleAzione={onToggleAzione}
        onReassignAzione={reassignAzione}
      />
    </div>
  );
}

function WeekDayColumn({
  iso, idx, isToday, isDropTarget, dayMit, dayAzioni, isoWeek,
  onDragEnter, onDragLeave, onDrop,
  onToggleMit, onToggleAzione, onReassignMit, onReassignAzione,
}) {
  const done = dayMit.filter((m) => m.fatto).length;
  return (
    <div
      className={`group border min-h-[200px] p-3 flex flex-col gap-2 transition-colors ${isDropTarget ? 'border-ok bg-ok/10' : 'border-line'}`}
      style={{ backgroundColor: isDropTarget ? undefined : (isToday ? 'rgb(var(--qp-ink-rgb) / 0.04)' : undefined) }}
      data-testid={`cal-week-day-${iso}`}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; onDragEnter(); }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        const payload = e.dataTransfer.getData('application/x-qp-mit');
        if (payload) onDrop(payload);
      }}
    >
      <div className="flex items-start justify-between">
        <Link
          to={`/periodo/giorno/${iso}`}
          className={
            'inline-flex items-baseline gap-2 hover:underline ' +
            (isToday ? 'text-ink' : 'text-soft')
          }
          data-testid={`cal-week-day-link-${iso}`}
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.18em]">{GIORNI_FULL[idx].slice(0, 3)}</span>
          <span className={'font-display text-lg tabular-nums ' + (isToday ? 'bg-ink text-canvas px-1.5' : '')}>
            {iso.slice(-2)}
          </span>
        </Link>
        <div className="flex items-center gap-1.5">
          {dayMit.length > 0 && (
            <span className="font-mono text-[9px] text-mute">{done}/{dayMit.length}</span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              window.dispatchEvent(new CustomEvent('qp:quick-add-open', {
                detail: { kind: 'mit', dataIso: iso },
              }));
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-ink hover:text-canvas text-soft border border-line hover:border-ink"
            title={`Aggiungi MIT o azione · ${iso}`}
            aria-label="Aggiungi MIT o azione"
            data-testid={`cal-week-day-add-${iso}`}
          >
            <Plus size={10} strokeWidth={2} />
          </button>
        </div>
      </div>
      <div className="space-y-1.5 flex-1">
        {dayMit.map((m) => (
          <WeekMitRow key={m.id} mit={m} onToggleMit={onToggleMit} onReassignMit={onReassignMit} />
        ))}
        {dayMit.length === 0 && dayAzioni.length === 0 && (
          <span className="font-mono text-[10px] text-mute italic">Nessuna MIT</span>
        )}
        {dayAzioni.map((a) => (
          <WeekAzioneFixedRow
            key={a.id}
            azione={a}
            isoWeek={isoWeek}
            onToggleAzione={onToggleAzione}
            onReassignAzione={onReassignAzione}
          />
        ))}
      </div>
    </div>
  );
}

function WeekMitRow({ mit: m, onToggleMit, onReassignMit }) {
  return (
    <div
      className="group flex items-start gap-1.5 text-[12px] leading-tight cursor-move hover:bg-[#FAFAFA] -mx-1 px-1 rounded transition-colors"
      data-testid={`cal-week-mit-${m.id}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/x-qp-mit', JSON.stringify({ id: m.id, currentData: m.data }));
      }}
    >
      <Checkbox
        checked={m.fatto}
        onToggle={() => onToggleMit(m.id, m.fatto)}
        size={14}
        label={m.testo}
        testid={`cal-week-mit-toggle-${m.id}`}
      />
      <span className="font-mono text-[8px] uppercase tracking-[0.15em] text-canvas bg-ink shrink-0 mt-0.5 px-1 py-px" aria-label="MIT">MIT</span>
      <DateAssignPopover
        kind="mit"
        currentValue={m.data}
        onSave={(nv) => onReassignMit(m.id, m.data, nv)}
        side="right"
        testid={`cal-mit-popover-${m.id}`}
      >
        <button
          type="button"
          className="flex-1 text-left"
          style={{
            textDecoration: m.fatto ? 'line-through' : 'none',
            color: m.fatto ? 'var(--qp-text-mute)' : 'var(--qp-ink)',
            opacity: m.fatto ? 0.55 : 1,
          }}
          data-testid={`cal-mit-trigger-${m.id}`}
        >
          {m.testo}
        </button>
      </DateAssignPopover>
    </div>
  );
}

function WeekAzioneFixedRow({ azione: a, isoWeek, onToggleAzione, onReassignAzione }) {
  const meta = AREE[(a.area || '').toLowerCase()] || { color: 'var(--qp-text-soft)' };
  return (
    <div
      className="flex items-start gap-1.5 text-[12px] leading-tight hover:bg-[#FAFAFA] -mx-1 px-1 rounded transition-colors border-l-2"
      style={{ borderLeftColor: meta.color }}
      data-testid={`cal-week-day-azione-${a.id}`}
      title="Azione fissata in questo giorno"
    >
      <Checkbox
        checked={a.fatto}
        onToggle={() => onToggleAzione(a.id, a.fatto)}
        size={14}
        label={a.testo}
        testid={`cal-week-day-azione-toggle-${a.id}`}
      />
      <span className="font-mono text-[8px] uppercase tracking-[0.15em] text-soft border border-soft shrink-0 mt-0.5 px-1 py-px" aria-label="Azione">AZ</span>
      <DateAssignPopover
        kind="azione"
        currentValue={a.data || a.settimana || isoWeek}
        onSave={(nv) => onReassignAzione(a.id, a.settimana || isoWeek, nv)}
        side="right"
        testid={`cal-week-day-azione-popover-${a.id}`}
      >
        <button
          type="button"
          className="flex-1 text-left italic"
          style={{
            textDecoration: a.fatto ? 'line-through' : 'none',
            color: a.fatto ? 'var(--qp-text-mute)' : 'var(--qp-ink)',
            opacity: a.fatto ? 0.55 : 1,
          }}
          data-testid={`cal-week-day-azione-trigger-${a.id}`}
        >
          {a.testo}
        </button>
      </DateAssignPopover>
    </div>
  );
}

function FloatingAzioniList({ azioniFloating, azioniTotali, isoWeek, onToggleAzione, onReassignAzione }) {
  return (
    <div className="mt-8" data-testid="cal-week-azioni">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display text-2xl tracking-tight">
          Azioni della settimana · {azioniFloating.length}
          {azioniTotali.length !== azioniFloating.length ? ` (+${azioniTotali.length - azioniFloating.length} fissate)` : ''}
        </h2>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('qp:quick-add-open', {
            detail: { kind: 'azione', settimana: isoWeek },
          }))}
          className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-line text-soft hover:border-ink hover:text-ink transition-colors inline-flex items-center gap-1"
          data-testid="cal-week-add-azione"
          title={`Aggiungi azione · ${isoWeek}`}
        >
          <Plus size={10} strokeWidth={2} />
          Aggiungi azione
        </button>
      </div>
      <ul className="divide-y divide-linesoft border-t border-b border-linesoft">
        {azioniFloating.map((a) => {
          const meta = AREE[(a.area || '').toLowerCase()] || { color: 'var(--qp-text-soft)', label: a.area || '—' };
          return (
            <li key={a.id} className="py-2.5 flex items-center gap-3" data-testid={`cal-week-azione-${a.id}`}>
              <Checkbox
                checked={a.fatto}
                onToggle={() => onToggleAzione(a.id, a.fatto)}
                size={16}
                label={a.testo}
                testid={`cal-week-azione-toggle-${a.id}`}
              />
              <span
                className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border shrink-0"
                style={{ borderColor: meta.color, color: meta.color }}
              >
                {a.is_bau ? 'BAU' : meta.label.slice(0, 3)}
              </span>
              <span
                className="flex-1 font-body text-sm"
                style={{
                  textDecoration: a.fatto ? 'line-through' : 'none',
                  color: a.fatto ? 'var(--qp-text-mute)' : undefined,
                  opacity: a.fatto ? 0.55 : 1,
                }}
              >
                {a.testo}
              </span>
              <DateAssignPopover
                kind="azione"
                currentValue={a.settimana || isoWeek}
                onSave={(nv) => onReassignAzione(a.id, a.settimana || isoWeek, nv)}
                side="left"
                testid={`cal-azione-popover-${a.id}`}
              >
                <button
                  type="button"
                  className="font-mono text-[10px] tabular-nums text-soft hover:text-ink hover:bg-linesoft px-1.5 py-0.5 rounded transition-colors shrink-0"
                  title="Sposta questa azione a un'altra settimana"
                  data-testid={`cal-azione-week-chip-${a.id}`}
                >
                  {(a.settimana || isoWeek).slice(-3)}
                </button>
              </DateAssignPopover>
            </li>
          );
        })}
        {azioniFloating.length === 0 && (
          <li className="py-6 text-center font-body text-sm text-mute italic">
            {azioniTotali.length > 0
              ? 'Tutte le azioni di questa settimana sono fissate a un giorno specifico.'
              : 'Nessuna azione pianificata per questa settimana.'}
          </li>
        )}
      </ul>
    </div>
  );
}

// ── Helper privati ──────────────────────────────────────────────────────

/** Raggruppa item per il campo `data` (ISO YYYY-MM-DD). Skippa item senza data. */
function _groupByData(items) {
  const m = {};
  for (const x of items) {
    if (x.data) (m[x.data] = m[x.data] || []).push(x);
  }
  return m;
}
